import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { injectIntl } from 'react-intl';
import _ from 'lodash';
import Select from 'react-select';
import { List, Map } from 'immutable';
import classNames from 'classnames';
import { connect } from 'react-redux';
import { Col, Row, Form, FormGroup, FormControl } from 'react-bootstrap';
import Button from '../ui/Button';
import Loader from '../ui/Loader';
import SearchAdvancedForm from './SearchAdvancedForm';
import { retrieveMetatags } from '../../actions/session';
import { retrieveAdvancedSearch, saveSearch, retrieveSavedSearchList, deleteSavedSearch } from '../../actions/umoGallery';
import { SearchTerm, AdvancedSearchTerm, AllowedTypeFilters, getTypeLabel, SearchParams } from '../../utils/searchUtils';
import { DEFAULT_COLLECTION_ID } from '../../constants/session';

const mapDispatchToProps = dispatch => ({
  dispatch: actionToDispatch => dispatch(actionToDispatch),
  retrieveMetatagsDispatch: (collectionID) => { dispatch(retrieveMetatags(collectionID)); },
});

/** @param {{rw:string}} saved search */
class SearchAdvanced extends Component {
  constructor(props) {
    super(props);

    this.state = {
      defaultCollectionID: this.props.userCollection || DEFAULT_COLLECTION_ID,
      selectedCollectionID: this.props.userCollection || DEFAULT_COLLECTION_ID,
      metatagList: new List(),
      isMetatagsLoading: false,
      metatagError: null,
      metadataList: [],
      searchName: '',
      selectedSearch: '',
      searchNameInvalid: false,
      searchSaveMessage: null,
      mediaType: null,
      searchCurCollection: false,
    };

    this.onCollectionChange = this.onCollectionChange.bind(this);
    this.setCollectionScope = this.setCollectionScope.bind(this);
    this.addMetadataRow = this.addMetadataRow.bind(this);
    this.onAddClick = this.onAddClick.bind(this);
    this.onRemoveClick = this.onRemoveClick.bind(this);
    this.removeMetadataRow = this.removeMetadataRow.bind(this);
    this.onRowChange = this.onRowChange.bind(this);
    this.onSubmit = this.onSubmit.bind(this);
    this.clearMetadataRowList = this.clearMetadataRowList.bind(this);
    this.clearAll = this.clearAll.bind(this);
    this.onSearchChange = this.onSearchChange.bind(this);
    this.saveCurrentSearch = this.saveCurrentSearch.bind(this);
    this.retrieveSavedSearch = this.retrieveSavedSearch.bind(this);
    this.deleteSavedSearch = this.deleteSavedSearch.bind(this);
    this.onMediaTypeChange = this.onMediaTypeChange.bind(this);
  }

  componentDidMount() {
    this.props.retrieveMetatagsDispatch(this.state.defaultCollectionID);
    if (!this.props.isPublicUsr) this.props.dispatch(retrieveSavedSearchList());
  }

  componentDidUpdate(prevProps, prevState) {
    if (prevProps.userCollection !== this.props.userCollection) {
      this.setState({
        defaultCollectionID: this.props.userCollection,
        selectedCollectionID: this.props.userCollection,
      });
      this.props.retrieveMetatagsDispatch(this.props.userCollection);
    }
    if (prevProps.metatags !== this.props.metatags) {
      const metatagList = this.props.metatags.get(this.state.selectedCollectionID);
      if (metatagList instanceof List) {
        this.setState({ metatagList });
        this.clearMetadataRowList();
      } else if (metatagList instanceof Error || !metatagList) {
        this.setState({
          metatagList: new List(),
          isMetatagsLoading: false,
          metatagError: metatagList,
          metadataList: [],
        });
      } else { // assume loading
        this.setState({
          metatagList: new List(),
          isMetatagsLoading: true,
          metatagError: null,
          metadataList: [],
        });
      }
    }

    if (prevProps.searchParams !== this.props.searchParams &&
      this.props.searchParams.get('metadataList')) {
      this.setState({ metadataList: Object.assign([], this.props.searchParams.get('metadataList')) });
    }
  }

  onAddClick(e) {
    e.preventDefault();
    this.addMetadataRow();
  }

  onRowChange(rowIndex, searchTerm) {
    const metaList = this.state.metadataList;
    metaList[rowIndex] = searchTerm;
    this.setState({ metadataList: metaList, selectedSearch: '' });
  }

  onRemoveClick(e, rowIndex) {
    e.preventDefault();
    this.removeMetadataRow(rowIndex);
  }

  onCollectionChange(e) {
    if (e !== this.state.selectedCollectionID) {
      this.setState({ selectedCollectionID: e.value });
      this.props.retrieveMetatagsDispatch(e.value);
    }
  }

  setCollectionScope() {
    this.setState({ searchCurCollection: !this.state.searchCurCollection });
  }

  onSubmit(e) {
    if (e) e.preventDefault();
    this.props.dispatch(retrieveAdvancedSearch(
      this.state.metadataList, this.state.mediaType, this.state.searchCurCollection ? this.state.selectedCollectionID : null));
    this.props.onAdvancedSearchToggle();
  }

  onSearchChange(search) {
    this.setState({ selectedSearch: search && search.value ? search : '', mediaType: null });
    if (search) {
      this.retrieveSavedSearch(search.value);
    }
  }

  addMetadataRow(searchTerm) {
    const metaList = Object.assign([], this.state.metadataList);
    metaList.push(searchTerm || new SearchTerm('DEFAULT'));
    this.setState({ metadataList: metaList, selectedSearch: '' });
    // todo: set limit for number of rows
  }

  removeMetadataRow(rowIndex) {
    const metaList = Object.assign([], this.state.metadataList);
    if (metaList.length === 1) {
      this.clearMetadataRowList();
    } else {
      metaList.splice(_.toInteger(rowIndex), 1);
      this.setState({
        metadataList: metaList,
        selectedSearch: '',
      });
    }
  }

  clearMetadataRowList(addNew = true) {
    this.setState({
      isMetatagsLoading: false,
      metatagError: null,
      metadataList: [],
      searchNameInvalid: false,
      searchName: '',
      selectedSearch: '',
      searchSaveMessage: null,
    }, addNew ? () => this.addMetadataRow() : null);
  }

  clearAll() {
    this.clearMetadataRowList();
    this.setState({ selectedCollectionID: this.state.defaultCollectionID, mediaType: null });
  }

  saveCurrentSearch() {
    if (_.isEmpty(this.state.searchName)) {
      this.setState({
        searchNameInvalid: true,
        searchSaveMessage: this.props.intl.formatMessage({ id: 'searchBar.advanced.searchNameMissing' }) });
    } else {
      this.props.dispatch(saveSearch(
        this.state.metadataList, this.state.mediaType,
        this.state.searchCurCollection ? this.state.selectedCollectionID : null, this.state.searchName))
        .then((res) => {
          if (res === 'save exists') {
            this.setState({
              searchNameInvalid: true,
              searchSaveMessage: this.props.intl.formatMessage({ id: 'searchBar.advanced.saveSearchExists' }),
            });
          } else this.setState({ searchNameInvalid: false, searchName: '', searchSaveMessage: null });
        })
        .catch(() => {
          this.setState({ searchSaveMessage: this.props.intl.formatMessage({ id: 'searchBar.advanced.searchSaveError' }) });
        });
    }
  }

  getRangeValueFromQuery(rangeQuery) {
    let rangeVal = '';
    switch (rangeQuery[2]) {
      case 'BETWEEN':
        rangeVal = `RANGE:${rangeQuery[3]},${rangeQuery[4]}`;
        break;
      case '<':
        rangeVal = `LESS:${rangeQuery[3]}`;
        break;
      case '>':
        rangeVal = `GREATER:${rangeQuery[3]}`;
        break;
      default:
        break;
    }
    return rangeVal;
  }

  retrieveSavedSearch(search) {
    let metaSearch;
    let metaVal;
    let metaTag;
    let multiVal;
    let collectionID;
    const metaOptions = {};
    const metaList = [];
    _.forEach(_.split(search, '" '), (value) => {
      metaSearch = _.split(value.replace('"', ''), /(META_NOT|META_RANGE|META_RANGE_NOT|META):/);
      // in case "Any metatag" was used
      if (metaSearch.length === 1) {
        metaSearch = _.split(metaSearch, 'IMAGE_TYPE:');
        if (metaSearch.length === 1) {
          metaSearch[0] = metaSearch[0].replace('"', '');
          metaSearch = _.split(metaSearch[0], /(WORDS_NOT|WORDS):/);
          metaOptions.operator = metaSearch[1] === 'WORDS_NOT' ? 'NOT' : metaSearch[0].replace(/\s+/g, '');
          metaList.push(new SearchTerm(
            'DEFAULT',
            metaSearch[2],
            metaOptions
          ));
        } else {
          metaSearch[1] = metaSearch[1].replace('"', '');
          this.setState({ mediaType: metaSearch[1] });
        }
      } else {
        metaVal = _.split(metaSearch[2].replace('"', ''), ',');
        metaSearch[0] = metaSearch[1].includes('NOT') ? 'NOT' : metaSearch[0];
        metaTag = _.split(metaVal[0], '.');
        if (metaSearch[1].includes('RANGE')) {
          metaOptions.type = metaVal[1];
          metaVal[1] = this.getRangeValueFromQuery(metaVal);
        }
        multiVal = _.split(metaVal[1], / (OR|AND) /);
        // for loop in case query has been optimized and parts were removed-
        // meta:xx AND meta:yy comes back from piction as meta:xx AND yy
        for (let i = 0; i < multiVal.length; i++) {
          // if first word- get operator of query, else get operator of word
          metaOptions.operator = i === 0 ? metaSearch[0].replace(/\s+/g, '') : multiVal[i - 1];
          metaList.push(new AdvancedSearchTerm(
            metaTag[0],
            metaTag[1],
            multiVal[i],
            metaOptions,
          ));
          i++;
        }
      }
    });
    this.setState({ metadataList: metaList }, () => this.onSubmit());
  }

  deleteSavedSearch() {
    this.props.dispatch(deleteSavedSearch(this.state.selectedSearch.remove))
      .then(() => this.props.dispatch(retrieveSavedSearchList()));
    this.setState({ selectedSearch: '' });
  }

  onMediaTypeChange(e) {
    this.setState({ mediaType: e.target.value });
  }

  render() {
    const { intl } = this.props;
    const collectionItems = [];
    if (this.props.collectionList) {
      this.props.collectionList.map(item => collectionItems.push({
        label: item.get('name'),
        value: item.get('id'),
        id: item.get('id'),
      }));
    }

    const metadataRowList = this.state.metadataList ?
      _.map(this.state.metadataList, (searchTerm, index) =>
        <SearchAdvancedForm
          rowIndex={index}
          onChange={this.onRowChange}
          onRemoveClick={this.onRemoveClick}
          searchTerm={searchTerm}
          metatagList={this.state.metatagList}
          key={index}
        />
      ) : null;

    const savedSearches = [];
    if (this.props.savedSearches) {
      this.props.savedSearches.map(item =>
        savedSearches.push({
          value: item.s[0].rw[0],
          remove: item.s[0].q[0],
          label: item.s[0].n[0],
        })
      );
    }

    const typeMenuItems = ['', ...AllowedTypeFilters].map(type => (
      <option key={type || ''} value={type}>
        {intl.formatMessage({ id: getTypeLabel(type) })}
      </option>
      ),
    );
    return (
      <div
        className={classNames('search-advanced',
        { 'search-advanced--open': this.props.isAdvancedSearchShown })}
      >
        <Row><Col md={{ span: 3 }}><h3 className="search-advanced__title">{intl.formatMessage({ id: 'searchBar.advancedSearch' })}</h3></Col></Row>
        {this.state.isMetatagsLoading ? <Loader className="search-advanced__loader" isLarge /> :
        <Form className="form" onSubmit={this.onSubmit}>
          <Row>
            <Col md={{ span: 3, offset: 2 }}>
              <Form.Group>
                <Form.Check
                  className="search-advanced__collection-scope-select"
                  id="search-advanced__coll_scope_select"
                  type="checkbox"
                  onChange={this.setCollectionScope}
                  checked={this.state.searchCurCollection}
                  label={intl.formatMessage({ id: 'searchBar.advanced.specificCollection' })}
                />
              </Form.Group>
            </Col>
          </Row>
          <Row>
            <Col md={{ span: 3, offset: 2 }}>
              <FormGroup>
                <Select
                  value={collectionItems.find(obj => obj.value === this.state.selectedCollectionID)}
                  className="search-advanced__metatag-select"
                  placeholder={intl.formatMessage({ id: 'searchBar.advanced.selectCollectionPlaceholder' })}
                  onChange={this.onCollectionChange}
                  options={collectionItems}
                  openOnFocus
                  searchable={false}
                  isOptionDisabled={option => option.id === '0'}
                />
              </FormGroup>
            </Col>
            <Col md={{ span: 3 }} className="search-advanced__type">
              <label>{intl.formatMessage({ id: 'searchBar.advanced.mediaType' })}</label>
              <select
                className="search-advanced__type-input btn btn-default btn-sm"
                onChange={this.onMediaTypeChange}
                id="advanced-input-dropdown-addon"
                title={intl.formatMessage({ id: getTypeLabel(this.state.mediaType) })}
              >
                {typeMenuItems}
              </select>
            </Col>
            <Col md={{ span: 3 }}>
              <Form.Label>
                {
                  this.props.isRequestingCollections ? <Loader /> :
                  this.props.collectionsError &&
                  <Button
                    size="sm"
                    variant="danger"
                    onClick={this.props.retrieveCollectionsDispatch}
                  >
                    <span>
                      <i className="search-advanced__metatag-error" />
                      &nbsp;{intl.formatMessage({ id: 'searchBar.advanced.collectionsRetrieveError' })}
                    </span>
                  </Button>
                }
              </Form.Label>
            </Col>
          </Row>
          { !this.state.isMetatagsLoading && metadataRowList }
          <Row>
            <Col md={{ span: 2, offset: 2 }}>
              <FormGroup>
                <button
                  type="button"
                  onClick={this.onAddClick}
                  className="search-advanced__metatag-add btn-sm"
                >
                  <i className="search-advanced__metatag-icon" />
                  {intl.formatMessage({ id: 'searchBar.advanced.addMetatagButton' })}
                </button>
              </FormGroup>
            </Col>
          </Row>
          <Row>
            <Col md={{ span: 2, offset: 2 }}>
              <FormGroup>
                <Button
                  size="sm"
                  type="submit"
                  variant="primary"
                  className="search-advanced__search-button"
                >
                  {intl.formatMessage({ id: 'searchBar.advanced.searchButton' })}
                </Button>
                <Button
                  size="sm"
                  variant="default"
                  className="search-advanced__search-button"
                  onClick={this.clearAll}
                >
                  {intl.formatMessage({ id: 'searchBar.advanced.clearButton' })}
                </Button>
              </FormGroup>
            </Col>
            {this.props.isPublicUsr ? null :
            <Col md={{ span: 4, offset: 1 }}>
              <FormGroup className="form-inline search-advanced__save-search" validated={this.state.searchNameInvalid ? 'invalid' : ''}>
                <FormControl
                  placeholder="New Search Name"
                  onChange={e => this.setState({ searchName: e.target.value })}
                  value={this.state.searchName}
                  type="text"
                  className="search-advanced__metatag-input"
                  size="sm"
                />
                <Button onClick={this.saveCurrentSearch} variant="info" size="sm">
                  {intl.formatMessage({ id: 'searchBar.advanced.saveSearch' })}
                </Button>
                {this.state.searchSaveMessage ?
                  <Form.Control.Feedback type="invalid">{this.state.searchSaveMessage}</Form.Control.Feedback>
                    : null}
              </FormGroup>
            </Col>}
          </Row>
          {!this.props.isPublicUsr && this.props.savedSearches &&
          this.props.savedSearches.length > 0 ?
            <Row>
              <Col md={{ span: 4, offset: 5 }}>
                <label
                  className="search-advanced__saved-search-label"
                  htmlFor="search-advanced__saved-search-select"
                >
                  {intl.formatMessage({ id: 'searchBar.advanced.savedSearches' })}
                </label>
                <FormGroup className="search-advanced__saved-search form-inline">
                  <Select
                    value={this.state.selectedSearch}
                    className="search-advanced__saved-search-select"
                    placeholder={intl.formatMessage({ id: 'searchBar.advanced.savedSearches' })}
                    onChange={this.onSearchChange}
                    options={savedSearches}
                    openOnFocus
                    size="sm"
                  />
                  { this.state.selectedSearch !== '' ?
                    <Button
                      className="search-advanced__saved-search-button"
                      onClick={this.deleteSavedSearch}
                      variant="danger"
                      size="sm"
                    >
                      <span>
                        <i className="search-advanced__del-search-icon" />
                        {_.truncate(
                        intl.formatMessage(
                          { id: 'searchBar.advanced.deleteSearchButton' },
                          { searchName: this.state.selectedSearch.label }), { length: 15 })
                      }
                      </span>
                    </Button> : null}
                </FormGroup>
              </Col>
            </Row>
             : null}
        </Form>
        }
      </div>
    );
  }
}

SearchAdvanced.propTypes = {
  dispatch: PropTypes.func,
  collectionList: PropTypes.instanceOf(List),
  metatags: PropTypes.instanceOf(Map),
  isRequestingCollections: PropTypes.bool,
  collectionsError: PropTypes.instanceOf(Error),
  savedSearches: PropTypes.oneOfType([
    PropTypes.instanceOf(Array),
    PropTypes.instanceOf(List),
  ]),
  userCollection: PropTypes.string,
  isAdvancedSearchShown: PropTypes.bool,
  onAdvancedSearchToggle: PropTypes.func,
  isPublicUsr: PropTypes.bool,
  searchParams: PropTypes.instanceOf(SearchParams),
};

export default injectIntl(connect(null, mapDispatchToProps)(SearchAdvanced));



// WEBPACK FOOTER //
// ./src/js/components/SearchBar/SearchAdvanced.jsx